import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { AbstractMethodComponent } from '../abstract-method.component';
import { firstValueFrom } from 'rxjs';
import * as moment from 'moment-timezone';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ProductListFascade } from '../../../../fascades/product-list.fascade';

export interface KlarnaPaymentsInterface {
  init: (params: object) => void;
  load: (
    params: object,
    callback: (result: { show_form: boolean }) => void
  ) => void;
  authorize: (
    params: object,
    authorization: object,
    callback: (result: object) => void
  ) => void;
}

export interface KlarnaInstance {
  Payments: KlarnaPaymentsInterface;
}

export interface KlarnaOrderLineSubscription {
  name: string;
  interval: string;
  interval_count: number;
}

export interface KlarnaOrderLine {
  type: string;
  reference?: string;
  name: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  total_tax_amount: number;
  tax_rate: number;
  total_discount_amount?: number;
  subscription?: KlarnaOrderLineSubscription;
}

export interface KlarnaAttachment {
  content_type: 'application/vnd.klarna.internal.emd-v2+json';
  body: string;
}

export interface KlarnaAuthorizationData {
  purchase_country: string;
  purchase_currency: string;
  locale: string;
  order_amount: number;
  order_tax_amount: number;
  order_lines: KlarnaOrderLine[];
  billing_address: {
    given_name: string;
    family_name: string;
    street_address: string;
    city: string;
    postal_code: string;
    region: string;
    country: string;
    phone_no: string;
    email: string;
  };
  shipping_address?: {
    given_name: string;
    family_name: string;
    street_address: string;
    city: string;
    postal_code: string;
    region: string;
    country: string;
    phone_no: string;
    email: string;
  };
  attachment?: KlarnaAttachment;
}

export interface KlarnaAuthorizeResult {
  approved: boolean;
  authorization_token: string;
}

@Component({
  selector: 'orderform-payment-method-klarna',
  templateUrl: './klarna.component.html',
  styleUrls: ['./klarna.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KlarnaComponent),
      multi: true,
    },
  ],
})
export class KlarnaComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() config: {
    apiUrl: string;
    intentClientSession: string;
    intentClientSecret: string;
    locale: string;
    debugLabel: string;
    method: 'klarna';
    config: any;
  };
  /**
   * Hier muss beforeSubmit Event verwendet werden,
   * weil Klarna gültige Adress-Daten benötigt bei der Initiierung der Bezahlung!
   * @private
   */
  private _subscriptionBeforeSubmit =
    this._orderformFascade.beforeSubmit.subscribe(() => this._onBeforeSubmit());

  private _waitForLoad: Promise<void>;

  protected _debugLabel = 'Klarna';

  constructor(
    private _orderformFascade: OrderformFascade,
    private _productListFascade: ProductListFascade,
    injector: Injector
  ) {
    super(injector);
  }

  getKlarna(): KlarnaInstance {
    return (<any>window).Klarna;
  }

  ngOnInit(): void {
    this._waitForLoad = (async () => {
      if (!this.getKlarna()) {
        await this._loadExternalResources([this.config.apiUrl], [], true);
      }

      const klarna = this.getKlarna();
      klarna.Payments.init({
        client_token: this.config.intentClientSession,
      });

      this._orderformFascade.order$
        .pipe(
          map((order) => order.order_summary.payment_plan.billing_type),
          distinctUntilChanged(),
          takeUntil(this._destroy$)
        )
        .subscribe((billingType) => {
          document.getElementById('klarna-payments-container').innerHTML = '';

          klarna.Payments.load(
            {
              container: '#klarna-payments-container',
              payment_method_category:
                billingType === 'subscription' ? 'pay_now' : 'klarna',
            },
            (res) => {
              //console.log(...arguments);
              if (!res.show_form) {
                this._hidePaymentMethodWithError(res);
              }
            }
          );
        });
    })();

    this._debugLabel = this.config.debugLabel;
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    await this._waitForLoad;

    const klarna = this.getKlarna();

    const orderData = await firstValueFrom(this._orderformFascade.order$);

    const helperGetDataForAddressType = (isBilling: boolean) => {
      const clone = {
        givenName: 'buyer_address_first_name',
        surname: 'buyer_address_last_name',
        phoneNumber: 'buyer_address_phone_no',
        streetAddress: 'buyer_address_street',
        extendedAddress: 'buyer_address_street_number',
        locality: 'buyer_address_city',
        state: 'buyer_address_state',
        postalCode: 'buyer_address_zipcode',
        countryCodeAlpha2: 'buyer_address_country',
      };
      Object.keys(clone).forEach((key) => {
        if (isBilling) {
          clone[key] = orderData.order.billing_address[clone[key] + '_billing'];
        } else {
          clone[key] = orderData.order.shipping_address[clone[key]];
        }
      });
      return clone;
    };

    const shippingAddress = helperGetDataForAddressType(false);
    // Check is billing address active
    // if not, set billingAddress data to empty object because default would be the shipping address
    const billingAddress = orderData.order.has_billing_address
      ? helperGetDataForAddressType(true)
      : ({} as any);

    //const items: KlarnaOrderLine[] = [];

    const saveNumberConvert = (number) => Math.round(number * 100);
    const productList = await firstValueFrom(
      this._productListFascade.cartProductList$
    );
    const items: KlarnaOrderLine[] = productList
      .filter((productData) => {
        return productData.orderItem.quantity?.value > 0;
      })
      .map((productData) => {
        const gross = saveNumberConvert(productData.total_amounts.first.gross);
        const gross_undiscounted = saveNumberConvert(
          productData.total_amounts_undiscounted.first.gross
        );
        const net = saveNumberConvert(productData.total_amounts.first.net);

        let tax_rate = 0;
        if (
          productData.total_amounts.first.gross &&
          productData.total_amounts.first.net
        ) {
          tax_rate = Math.round(
            (productData.total_amounts.first.gross /
              productData.total_amounts.first.net -
              1) *
              10000
          );
        }

        return {
          type: productData.delivery_type == "shipping" ? "physical" : "digital",
          reference: '' + productData.product_id,
          name: productData.headline,
          quantity: productData.orderItem.quantity.value,
          unit_price: saveNumberConvert(productData.unit_amounts.first.gross),
          total_amount: gross,
          total_tax_amount: gross - net,
          tax_rate,
          total_discount_amount: gross_undiscounted - gross,
        };
      });

    items.sort((a, b) => parseInt(a.reference, 10) - parseInt(b.reference, 10));

    const authorizationData: KlarnaAuthorizationData = {
      purchase_country:
        billingAddress.countryCodeAlpha2 ||
        shippingAddress.countryCodeAlpha2 ||
        orderData.country ||
        '',
      purchase_currency: orderData.order.currency_code,
      locale: this.config.locale ?? orderData.settings.global.locale,
      order_amount: saveNumberConvert(
        orderData.order_summary.order_total.first.gross
      ),
      order_tax_amount: saveNumberConvert(
        orderData.order_summary.order_total.first.vat
      ),
      order_lines: items,
      billing_address: this.removeEmptyProperties({
        given_name: billingAddress.givenName || shippingAddress.givenName || '',
        family_name: billingAddress.surname || shippingAddress.surname || '',
        street_address: (
          (billingAddress.streetAddress ||
            shippingAddress.streetAddress ||
            '') +
          ' ' +
          (billingAddress.extendedAddress ||
            shippingAddress.extendedAddress ||
            '')
        ).trim(),
        city: billingAddress.locality || shippingAddress.locality || '',
        postal_code:
          billingAddress.postalCode || shippingAddress.postalCode || '',
        region: billingAddress.state || shippingAddress.state || '',
        country:
          billingAddress.countryCodeAlpha2 ||
          shippingAddress.countryCodeAlpha2 ||
          '',
        phone_no:
          billingAddress.phoneNumber || shippingAddress.phoneNumber || '',
        email: orderData.order.shipping_address.buyer_address_email,
      }),
    };

    if (
      orderData.settings.contact_form &&
      orderData.settings.contact_form.has_shipping
    ) {
      authorizationData.shipping_address = this.removeEmptyProperties({
        given_name: shippingAddress.givenName || '',
        family_name: shippingAddress.surname || '',
        street_address:
          (shippingAddress.streetAddress ?? '') +
          (shippingAddress.extendedAddress
            ? ' ' + shippingAddress.extendedAddress
            : ''),
        city: shippingAddress.locality || '',
        postal_code: shippingAddress.postalCode || '',
        region: shippingAddress.state || '',
        country: shippingAddress.countryCodeAlpha2 || '',
        phone_no: shippingAddress.phoneNumber || '',
        email: orderData.order.shipping_address.buyer_address_email,
      });
    }

    const voucher = this.removeEmptyProperties({
      voucher_name: orderData.settings.product.items.find(
        (product) => product.product_id === orderData.settings.config.product_id
      ).headline,
      voucher_company: orderData.merchant_name,
      affiliate_name:
        orderData.settings.affiliate.name !== 'none'
          ? orderData.settings.affiliate.name
          : '',
      start_date: moment(
        new Date(orderData.order_summary.payment_plan.first_payment_at)
      ),
    });

    if (orderData.order_summary.payment_plan.billing_type === 'subscription') {
      const [first_interval_qty, first_interval_unit] =
        orderData.order_summary.payment_plan.first_billing_interval.split('_');
      const [interval_qty, interval_unit] =
        orderData.order_summary.payment_plan.other_billing_intervals.split('_');

      const startDate = moment(
        new Date(orderData.order_summary.payment_plan.first_payment_at)
      )
        .add(
          parseInt(first_interval_qty, 10),
          first_interval_unit as moment.DurationInputArg2
        )
        .toDate();

      const endDate = moment(
        new Date(orderData.order_summary.payment_plan.first_payment_at)
      )
        .add(
          parseInt(interval_qty, 10),
          interval_unit as moment.DurationInputArg2
        )
        .toDate();

      const subscription = [];

      authorizationData.order_lines.forEach((item) => {
        const productData = orderData.settings.product.items.find(
          (product) => '' + product.product_id === '' + item.reference
        );
        if (productData.total_amounts.oth.gross !== 0) {
          item.subscription = {
            name: productData.headline,
            interval: interval_unit.toUpperCase(),
            interval_count: parseInt(interval_qty, 10),
          };

          subscription.push(
            this.removeEmptyProperties({
              subscription_name: productData.headline,
              start_date: startDate.toISOString().split('.')[0],
              end_date: endDate.toISOString().split('.')[0],
              auto_renewal_of_subscription: true,
              affiliate_name:
                orderData.settings.affiliate.name !== 'none'
                  ? orderData.settings.affiliate.name
                  : '',
            })
          );
        }
      });
      authorizationData.attachment = {
        content_type: 'application/vnd.klarna.internal.emd-v2+json',
        body: JSON.stringify({
          subscription: subscription,
          voucher: [voucher],
        }),
      };
    } else {
      authorizationData.attachment = {
        content_type: 'application/vnd.klarna.internal.emd-v2+json',
        body: JSON.stringify({ voucher: [voucher] }),
      };
    }

    if (orderData.order_summary.shipping_total.first.gross) {
      authorizationData.order_lines.push({
        quantity: 1,
        tax_rate: Math.round(
          (orderData.order_summary.shipping_total.first.gross /
            orderData.order_summary.shipping_total.first.net -
            1) *
            10000
        ),
        name: 'Shipping Fee',
        total_amount: Math.round(orderData.order_summary.shipping_total.first.gross * 100),
        total_tax_amount: Math.round(
          orderData.order_summary.shipping_total.first.gross * 100 -
            orderData.order_summary.shipping_total.first.net * 100
        ),
        unit_price: Math.round(orderData.order_summary.shipping_total_undiscounted.first.gross  * 100),
        type: 'shipping_fee',
        total_discount_amount: Math.round((orderData.order_summary.shipping_total_undiscounted.first.gross - orderData.order_summary.shipping_total.first.gross) * 100),
        reference: 'shipping',
      });
    }

    return new Promise<KlarnaAuthorizeResult>((resolve, reject) => {
      klarna.Payments.authorize(
        {
          payment_method_category: 'klarna',
        },
        authorizationData,
        resolve
      );
    }).then((result) => {
      if (result.approved) {
        this._paymentDataChanged.next({
          valid: true,
          klarna_session: this.config.intentClientSession,
          klarna_client_authorization: result.authorization_token,
          authorizationData,
        });
        return { valid: true };
      } else {
        //console.log({ result, ...arguments });
        return { valid: false };
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._destroy$.next();
  }

  private removeEmptyProperties<T>(obj: T): T {
    return Object.keys(obj).reduce((clonedObject, key) => {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== '') {
        clonedObject[key] = value;
      }
      return clonedObject;
    }, {} as any);
  }
}
